import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCopy,
  FaEnvelope,
  FaFacebookF,
  FaLinkedinIn,
  FaShareAlt,
  FaTelegramPlane,
  FaTimes,
  FaWhatsapp,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { toast } from "react-toastify";
import marketplaceProductShareService from "../../services/marketplaceProductShare.service";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import {
  MARKETPLACE_SHARE_PLATFORMS,
  buildAbsoluteShareUrl,
  buildEmailShareUrl,
  buildFacebookShareUrl,
  buildLinkedInShareUrl,
  buildProductSharePath,
  buildProductSharePayload,
  buildTelegramShareUrl,
  buildTwitterShareUrl,
  buildWhatsAppShareUrl,
  openShareWindow,
} from "../../utils/marketplaceProductShare.util";

function ShareOptionButton({ option, onClick }) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      className="marketplace-product-share__option"
      role="menuitem"
      onClick={() => onClick(option.id)}
    >
      <span className={`marketplace-product-share__option-icon ${option.iconClass}`}>
        <Icon aria-hidden />
      </span>
      <span className="marketplace-product-share__option-copy">
        <span className="marketplace-product-share__option-label">{option.label}</span>
        <span className="marketplace-product-share__option-subtitle">{option.subtitle}</span>
      </span>
    </button>
  );
}

export default function MarketplaceProductShareButton({
  productId,
  title,
  sharePath,
  imagePath = "",
  imageUrl = "",
  companyName = "",
  description = "",
  className = "",
  triggerClassName = "marketplace-product-row__btn marketplace-product-row__btn--outline marketplace-product-share__trigger",
  triggerLabel = "Share",
}) {
  const [open, setOpen] = useState(false);

  const resolvedSharePath = buildProductSharePath(productId, sharePath);
  const shareUrl = buildAbsoluteShareUrl(resolvedSharePath);
  const resolvedImageUrl = useMemo(() => {
    const raw = String(imageUrl || imagePath || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return buildAbsoluteShareUrl(resolveHubImage(raw));
  }, [imagePath, imageUrl]);

  const payload = useMemo(
    () =>
      buildProductSharePayload({
        title,
        shareUrl,
        imageUrl: resolvedImageUrl,
        companyName,
        description,
      }),
    [title, shareUrl, resolvedImageUrl, companyName, description],
  );

  const shareOptions = useMemo(() => {
    const options = [
      {
        id: MARKETPLACE_SHARE_PLATFORMS.WHATSAPP,
        label: "WhatsApp",
        subtitle: "Share on WhatsApp",
        icon: FaWhatsapp,
        iconClass: "marketplace-product-share__option-icon--whatsapp",
      },
      {
        id: MARKETPLACE_SHARE_PLATFORMS.LINKEDIN,
        label: "LinkedIn",
        subtitle: "Share on LinkedIn",
        icon: FaLinkedinIn,
        iconClass: "marketplace-product-share__option-icon--linkedin",
      },
      {
        id: MARKETPLACE_SHARE_PLATFORMS.FACEBOOK,
        label: "Facebook",
        subtitle: "Share on Facebook",
        icon: FaFacebookF,
        iconClass: "marketplace-product-share__option-icon--facebook",
      },
      {
        id: MARKETPLACE_SHARE_PLATFORMS.TWITTER,
        label: "X (Twitter)",
        subtitle: "Share on X",
        icon: FaXTwitter,
        iconClass: "marketplace-product-share__option-icon--twitter",
      },
      {
        id: MARKETPLACE_SHARE_PLATFORMS.TELEGRAM,
        label: "Telegram",
        subtitle: "Share on Telegram",
        icon: FaTelegramPlane,
        iconClass: "marketplace-product-share__option-icon--telegram",
      },
      {
        id: MARKETPLACE_SHARE_PLATFORMS.EMAIL,
        label: "Email",
        subtitle: "Share via Email",
        icon: FaEnvelope,
        iconClass: "marketplace-product-share__option-icon--email",
      },
    ];

    return options;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const trackShare = (platform) => {
    marketplaceProductShareService.trackShare(productId, platform);
  };

  const closeMenu = () => {
    setOpen(false);
  };

  const copyProductLink = async (shouldTrack = true) => {
    if (shouldTrack) trackShare(MARKETPLACE_SHARE_PLATFORMS.COPY_LINK);
    try {
      await navigator.clipboard.writeText(payload.shareUrl);
      toast.success("Product link copied successfully.");
    } catch {
      toast.error("Unable to copy link.");
    }
  };

  const handleShareAction = async (platform) => {
    trackShare(platform);

    switch (platform) {
      case MARKETPLACE_SHARE_PLATFORMS.WHATSAPP:
        openShareWindow(buildWhatsAppShareUrl(payload));
        closeMenu();
        return;
      case MARKETPLACE_SHARE_PLATFORMS.FACEBOOK:
        openShareWindow(buildFacebookShareUrl(payload.shareUrl));
        closeMenu();
        return;
      case MARKETPLACE_SHARE_PLATFORMS.LINKEDIN:
        openShareWindow(buildLinkedInShareUrl(payload.shareUrl));
        closeMenu();
        return;
      case MARKETPLACE_SHARE_PLATFORMS.TWITTER:
        openShareWindow(buildTwitterShareUrl(payload));
        closeMenu();
        return;
      case MARKETPLACE_SHARE_PLATFORMS.TELEGRAM:
        openShareWindow(buildTelegramShareUrl(payload));
        closeMenu();
        return;
      case MARKETPLACE_SHARE_PLATFORMS.EMAIL:
        window.location.href = buildEmailShareUrl(payload);
        closeMenu();
        return;
      default:
        return;
    }
  };

  const handleTriggerClick = () => {
    setOpen((value) => !value);
  };

  const panel = open ? (
    <div
      className="marketplace-product-share__overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeMenu();
      }}
    >
      <div className="marketplace-product-share__modal" role="dialog" aria-modal="true" aria-label="Share product">
        <div className="marketplace-product-share__modal-head">
          <h2>Share this product</h2>
          <button type="button" className="marketplace-product-share__close" onClick={closeMenu} aria-label="Close share dialog">
            <FaTimes aria-hidden />
          </button>
        </div>

        <div className="marketplace-product-share__preview">
          {resolvedImageUrl ? (
            <img src={resolvedImageUrl} alt={payload.productName} />
          ) : (
            <div className="marketplace-product-share__preview-empty">No image</div>
          )}
          <div className="marketplace-product-share__preview-body">
            <h3>{payload.productName}</h3>
            {payload.companyName ? (
              <p className="marketplace-product-share__supplier">
                Supplier: <strong>{payload.companyName}</strong>
              </p>
            ) : null}
            <div className="marketplace-product-share__url">
              <span>{payload.shareUrl}</span>
              <button type="button" onClick={() => void copyProductLink()} aria-label="Copy product link">
                <FaCopy aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <div className="marketplace-product-share__grid" role="menu">
          {shareOptions.map((option) => (
            <ShareOptionButton key={option.id} option={option} onClick={handleShareAction} />
          ))}
        </div>

        <div className="marketplace-product-share__footer">
          <p>When you share, others will see this product information and can view it on our marketplace.</p>
          <button type="button" onClick={closeMenu}>Close</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      className={`marketplace-product-share${className ? ` ${className}` : ""}`}
    >
      <button
        type="button"
        className={triggerClassName}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={handleTriggerClick}
      >
        <FaShareAlt aria-hidden /> {triggerLabel}
      </button>
      {typeof document !== "undefined" && panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
